<template>
  <div>
    <b-form @submit.prevent="submitForm">
      <b-row>
        <!-- Columna de Información General -->
        <b-col md="6">
          <b-card title="Información General" class="mb-4">
            <b-form-group label="Nombre del Programa:">
              <b-form-input
                v-model="form.name"
                placeholder="Ej: Descuento Clientes VIP"
                required
              ></b-form-input>
            </b-form-group>

            <b-form-group label="Tipo de Programa:">
              <b-form-select v-model="form.program_type">
                <b-form-select-option value="coupons">Cupón / Cortesía</b-form-select-option>
                <b-form-select-option value="discount">Descuento</b-form-select-option>
                <b-form-select-option value="buy_x_get_y">Compra X, Obtén Y (2x1, 3x2)</b-form-select-option>
                <b-form-select-option value="free_product">Producto Gratis</b-form-select-option>
                <b-form-select-option value="free_products">Productos Gratis (Múltiples / Tenedor libre)</b-form-select-option>
              </b-form-select>
              <small class="text-muted d-block mt-1">{{ programTypeHelp }}</small>
            </b-form-group>

            <b-form-group
              v-if="form.program_type === 'buy_x_get_y' || form.program_type === 'free_products'"
              label="Disparador:"
            >
              <b-form-select v-model="form.trigger">
                <b-form-select-option value="with_code">Con código (cajero ingresa código)</b-form-select-option>
                <b-form-select-option value="automatic">Automático (se aplica solo al cumplir la cantidad)</b-form-select-option>
              </b-form-select>
              <small class="text-muted d-block mt-1">
                Las promos automáticas se disparan en cuanto el ticket cumple la cantidad mínima de productos; las "con código" requieren que el cajero teclee el código.
              </small>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="form.active">
                Activo
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="form.limit_usage">
                Limitar uso (solo una promoción por ticket)
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="form.portal_visible">
                Visible en Portal
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="form.require_extra">
                Requiere información adicional al canjear
              </b-form-checkbox>
            </b-form-group>

            <template v-if="form.require_extra">
              <b-form-group label="Etiqueta del campo adicional:">
                <b-form-input
                  v-model="form.extra_description"
                  placeholder="Ej: Número de empleado, Compañía"
                ></b-form-input>
              </b-form-group>

              <b-form-group label="Tipo de campo:">
                <b-form-select v-model="form.extra_type">
                  <b-form-select-option value="text">Texto libre</b-form-select-option>
                  <b-form-select-option value="number">Número</b-form-select-option>
                  <b-form-select-option value="select">Lista de opciones</b-form-select-option>
                </b-form-select>
              </b-form-group>

              <b-form-group v-if="form.extra_type === 'text' || form.extra_type === 'number'" label="Validación (regex opcional):">
                <b-form-input
                  v-model="form.extra_regex"
                  placeholder="Ej: ^[0-9]{4}$ para 4 dígitos"
                ></b-form-input>
                <small class="text-muted">Déjalo vacío si no necesitas validar el formato.</small>
              </b-form-group>

              <b-form-group v-if="form.extra_type === 'select'" label="Opciones disponibles:">
                <div v-if="form.extra_options.length > 0" class="mb-2">
                  <b-badge
                    v-for="(opt, idx) in form.extra_options"
                    :key="idx"
                    variant="secondary"
                    class="mr-1 mb-1"
                  >
                    {{ opt }}
                    <b-button variant="link" size="sm" class="text-white p-0 ml-1" @click="removeExtraOption(opt, idx)">×</b-button>
                  </b-badge>
                </div>
                <b-input-group size="sm">
                  <b-form-input v-model="newExtraOption" placeholder="Ej: TV AZTECA"></b-form-input>
                  <b-input-group-append>
                    <b-button variant="outline-info" @click="addExtraOption" :disabled="!newExtraOption || !form.id">+</b-button>
                  </b-input-group-append>
                </b-input-group>
                <small v-if="!form.id" class="text-muted">Primero guarda el programa para poder añadir opciones.</small>
              </b-form-group>
            </template>
          </b-card>
        </b-col>

        <!-- Columna de Reglas y Recompensas -->
        <b-col md="6">
          <!-- Reglas -->
          <b-card title="Reglas de Obtención de Puntos" class="mb-4">
            <b-card bg-variant="light" class="mb-3">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Modo de Recompensa:">
                    <b-form-select v-model="currentRule.reward_pointmode">
                      <b-form-select-option value="order">Por Orden (1 punto por ticket que cumple)</b-form-select-option>
                      <b-form-select-option value="unit">Por Unidad de Producto</b-form-select-option>
                    </b-form-select>
                  </b-form-group>
                </b-col>

                <b-col md="6" v-if="ruleNeedsCode">
                  <b-form-group label="Código de Promoción:">
                    <b-form-input
                      v-model="currentRule.code"
                      placeholder="Ej: VERANO2026"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group v-if="currentRule.reward_pointmode === 'unit'" label="Cantidad Mínima de Productos:">
                <b-form-input
                  v-model.number="currentRule.minimum_qty"
                  type="number"
                  min="1"
                  step="1"
                ></b-form-input>
                <small class="text-muted">Para 2x1 usa 2, para 3x2 usa 3, etc.</small>
              </b-form-group>

              <b-form-group label="Monto Mínimo del Ticket (opcional):">
                <b-form-input
                  v-model.number="currentRule.minimum_amount"
                  type="number"
                  min="0"
                  step="0.01"
                ></b-form-input>
                <small class="text-muted">El ticket debe superar este monto para que aplique. 0 = sin mínimo.</small>
              </b-form-group>

              <b-form-group label="Puntos a Otorgar:">
                <b-form-input
                  v-model.number="currentRule.reward_point_amount"
                  type="number"
                  min="1"
                  step="1"
                ></b-form-input>
              </b-form-group>

              <div class="d-flex justify-content-between">
                <b-button variant="outline-primary" size="sm" @click="saveRules" :disabled="!form.id">
                  Guardar Reglas
                </b-button>
                <b-button variant="primary" size="sm" @click="addRule">
                  Añadir Regla
                </b-button>
              </div>
            </b-card>

            <div v-if="form.rules.length > 0">
              <h6>Reglas Añadidas</h6>
              <b-list-group class="mb-2">
                <b-list-group-item v-for="(rule, index) in form.rules" :key="index">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <div class="font-weight-bold">
                        {{ ruleNeedsCode ? `Código: ${rule.code}` : 'Regla Automática' }}
                      </div>
                      <small>
                        {{ rule.reward_pointmode === 'order' ? 'Puntos por orden' :
                           rule.reward_pointmode === 'unit' ? `Puntos por unidad (mín: ${rule.minimum_qty})` : '' }}
                        | Otorga {{ rule.reward_point_amount }} punto(s)
                        <span v-if="rule.minimum_amount > 0">| Ticket mín: ${{ rule.minimum_amount }}</span>
                      </small>
                    </div>
                    <b-button variant="outline-danger" size="sm" @click="removeRule(index)">
                      <i class="fas fa-trash"></i>
                    </b-button>
                  </div>
                  <!-- Productos elegibles para esta regla -->
                  <div v-if="rule.id && ruleNeedsProducts" class="mt-2 pl-3 border-left">
                    <small class="text-muted d-block mb-1">Productos elegibles:</small>
                    <div v-for="(rp, rpIndex) in (rule.rule_products || [])" :key="rpIndex" class="d-inline-flex align-items-center mr-2 mb-1">
                      <b-badge variant="info" class="d-flex align-items-center">
                        {{ productLabel(rp.product_id) }}
                        <b-button variant="link" size="sm" class="text-white p-0 ml-1" @click="removeRuleProduct(rule, rpIndex, rp.id)">×</b-button>
                      </b-badge>
                    </div>
                    <b-input-group size="sm" class="mt-1" style="max-width: 320px;">
                      <b-form-select v-model="newRuleProductId" :options="productSelectOptions">
                        <template #first>
                          <b-form-select-option :value="null">-- Selecciona un producto --</b-form-select-option>
                        </template>
                      </b-form-select>
                      <b-input-group-append>
                        <b-button variant="outline-info" @click="addRuleProduct(rule)" :disabled="!newRuleProductId">+</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div v-else class="text-center text-muted my-3">
              No hay reglas añadidas
            </div>
          </b-card>

          <!-- Recompensas -->
          <b-card title="Recompensas">
            <b-card bg-variant="light" class="mb-3">
              <b-form-group label="Tipo de Recompensa:">
                <b-form-select v-model="currentReward.reward_type">
                  <b-form-select-option value="discount">Descuento</b-form-select-option>
                  <b-form-select-option value="free_product">Producto Gratis</b-form-select-option>
                </b-form-select>
              </b-form-group>

              <template v-if="currentReward.reward_type === 'discount'">
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Modo de Descuento:">
                      <b-form-select v-model="currentReward.discount_mode">
                        <b-form-select-option value="percent">Porcentaje (%)</b-form-select-option>
                        <b-form-select-option value="per_order">Monto fijo ($)</b-form-select-option>
                        <b-form-select-option value="per_point">Monto variable por cajero ($/punto)</b-form-select-option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>

                  <b-col md="6">
                    <b-form-group label="Aplicable a:">
                      <b-form-select v-model="currentReward.discount_applicability">
                        <b-form-select-option value="order">Toda la Orden</b-form-select-option>
                        <b-form-select-option value="specific">Productos Específicos (los de la regla)</b-form-select-option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group :label="discountLabel">
                  <b-form-input
                    v-model.number="currentReward.discount"
                    type="number"
                    :min="currentReward.discount_mode === 'percent' ? 0 : 0"
                    :max="currentReward.discount_mode === 'percent' ? 100 : undefined"
                    :step="currentReward.discount_mode === 'percent' ? '1' : '0.01'"
                    required
                  ></b-form-input>
                  <small v-if="currentReward.discount_mode === 'percent'" class="text-muted">Entre 0 y 100.</small>
                  <small v-else-if="currentReward.discount_mode === 'per_point'" class="text-muted">El cajero teclea el monto al canjear; se usa 1.00 como referencia.</small>
                </b-form-group>

                <b-form-group label="Monto Máximo de Descuento:">
                  <b-form-input
                    v-model.number="currentReward.discount_max_amount"
                    type="number"
                    min="0"
                    step="0.01"
                  ></b-form-input>
                  <small class="text-muted">0 = Sin límite. Útil para topar un % alto (ej. 100% con tope de $200).</small>
                </b-form-group>
              </template>

              <template v-if="currentReward.reward_type === 'free_product'">
                <b-form-group label="Producto a Regalar:">
                  <b-form-select v-model="currentReward.reward_product_id" :options="productSelectOptions" required>
                    <template #first>
                      <b-form-select-option :value="null">-- Selecciona un producto --</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <b-form-group label="Cantidad a Regalar:">
                  <b-form-input
                    v-model.number="currentReward.reward_product_qty"
                    type="number"
                    min="1"
                    step="1"
                    required
                  ></b-form-input>
                </b-form-group>
              </template>

              <b-form-group label="Descripción de la Recompensa:">
                <b-form-input
                  v-model="currentReward.description"
                  placeholder="Ej: 15% de descuento en la cuenta"
                  required
                ></b-form-input>
              </b-form-group>

              <b-form-group label="Puntos Requeridos:">
                <b-form-input
                  v-model.number="currentReward.required_points"
                  type="number"
                  min="1"
                  step="1"
                  required
                ></b-form-input>
              </b-form-group>

              <div class="d-flex justify-content-between">
                <b-button variant="outline-primary" size="sm" @click="saveRewards" :disabled="!form.id">
                  Guardar Recompensas
                </b-button>
                <b-button variant="primary" size="sm" @click="addReward">
                  Añadir Recompensa
                </b-button>
              </div>
            </b-card>

            <div v-if="form.rewards.length > 0">
              <h6>Recompensas Añadidas</h6>
              <b-list-group class="mb-2">
                <b-list-group-item v-for="(reward, index) in form.rewards" :key="index" class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="font-weight-bold">{{ reward.description }}</div>
                    <small>
                      {{ reward.reward_type === 'discount' ?
                        `${reward.discount_mode === 'percent' ? `${reward.discount}%` :
                         reward.discount_mode === 'per_order' ? `$${reward.discount}` :
                         `$${reward.discount} por punto`} de descuento en ${reward.discount_applicability === 'order' ? 'toda la orden' : 'productos específicos'}` :
                        `${reward.reward_product_qty} ${reward.reward_product_qty > 1 ? 'unidades' : 'unidad'} de ${productLabel(reward.reward_product_id)}` }}
                      | Requiere {{ reward.required_points }} punto(s)
                    </small>
                  </div>
                  <b-button variant="outline-danger" size="sm" @click="removeReward(index)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div v-else class="text-center text-muted my-3">
              No hay recompensas añadidas
            </div>
          </b-card>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-between mt-4 pt-3 border-top">
        <b-button variant="outline-secondary" @click="$emit('cancel')">
          Cancelar
        </b-button>
        <b-button type="button" variant="primary" @click="createProgramOnly">
          {{ isEditing ? 'Guardar Información Básica' : 'Crear Programa' }}
        </b-button>
      </div>
    </b-form>
</div>
</template>

<script>
import axios from "axios";

const PROGRAM_TYPE_HELP = {
  coupons: "Códigos manuales tipo cortesía o convenio (ej. BANORTE10, EMPLEADO25, CORTESIA).",
  discount: "Descuento general con código (ej. LIVERPOOL 20% en producto específico).",
  buy_x_get_y: "Promos tipo 2x1 o 3x2. Pueden dispararse automáticamente o con código.",
  free_product: "Un producto regalado al canjear un código (ej. TACOGRATIS).",
  free_products: "Lista de productos regalados, ej. tenedor libre."
};

// Defaults inteligentes para evitar combinaciones que el motor local no procesa.
const DEFAULTS_BY_TYPE = {
  coupons: {
    trigger: "with_code",
    rule: { reward_pointmode: "order", minimum_qty: null },
    reward: { reward_type: "discount", discount_mode: "percent", discount_applicability: "order" }
  },
  discount: {
    trigger: "with_code",
    rule: { reward_pointmode: "order", minimum_qty: null },
    reward: { reward_type: "discount", discount_mode: "percent", discount_applicability: "order" }
  },
  buy_x_get_y: {
    trigger: "automatic",
    rule: { reward_pointmode: "unit", minimum_qty: 2 },
    reward: { reward_type: "free_product", discount_mode: "per_order", discount_applicability: "specific" }
  },
  free_product: {
    trigger: "with_code",
    rule: { reward_pointmode: "order", minimum_qty: null },
    reward: { reward_type: "free_product", discount_mode: "per_order", discount_applicability: "specific" }
  },
  free_products: {
    trigger: "with_code",
    rule: { reward_pointmode: "order", minimum_qty: null },
    reward: { reward_type: "free_product", discount_mode: "per_order", discount_applicability: "specific" }
  }
};

function blankRule() {
  return {
    reward_pointmode: "order",
    mode: "with_code",
    code: "",
    minimum_amount: 0,
    minimum_qty: null,
    active: true,
    reward_point_amount: 1
  };
}

function blankReward() {
  return {
    reward_type: "discount",
    discount_mode: "percent",
    discount_applicability: "order",
    description: "",
    discount_max_amount: 0,
    active: true,
    discount: 0,
    required_points: 1,
    reward_product_id: null,
    reward_product_qty: 1
  };
}

export default {
  name: "ProgramForm",
  props: {
    program: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isEditing: false,
      form: {
        name: "",
        program_type: "coupons",
        trigger: "with_code",
        max_usage: 0,
        active: true,
        limit_usage: true,
        portal_visible: false,
        require_extra: false,
        extra_description: "",
        extra_type: "text",
        extra_regex: "",
        extra_options: [],
        discount_line_product_id: "",
        rules: [],
        rewards: []
      },
      currentRule: blankRule(),
      currentReward: blankReward(),
      newRuleProductId: null,
      newExtraOption: "",
      products: []
    };
  },
  computed: {
    programTypeHelp() {
      return PROGRAM_TYPE_HELP[this.form.program_type] || "";
    },
    ruleNeedsCode() {
      return this.form.trigger === "with_code";
    },
    ruleNeedsProducts() {
      return this.form.program_type === "buy_x_get_y"
        || this.form.program_type === "free_products"
        || this.currentRule.reward_pointmode === "unit"
        || this.currentReward.discount_applicability === "specific";
    },
    discountLabel() {
      switch (this.currentReward.discount_mode) {
        case "percent": return "Porcentaje de Descuento (%):";
        case "per_order": return "Monto Fijo de Descuento ($):";
        case "per_point": return "Monto por Punto ($):";
        default: return "Descuento:";
      }
    },
    productSelectOptions() {
      return this.products.map(p => ({
        value: p.id,
        text: p.short_name || p.ndescription || `#${p.id}`
      }));
    }
  },
  watch: {
    program() {
      this.initializeForm();
    },
    "form.program_type"(newType, oldType) {
      if (!newType || newType === oldType) return;
      this.applyTypeDefaults(newType);
    }
  },
  created() {
    this.initializeForm();
    this.loadProducts();
  },
  methods: {
    async loadProducts() {
      try {
        const res = await axios.get("/productswithoutprices");
        this.products = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Error cargando productos:", e);
      }
    },
    productLabel(productId) {
      const p = this.products.find(x => x.id === productId);
      if (!p) return `#${productId}`;
      return p.short_name || p.ndescription || `#${productId}`;
    },
    applyTypeDefaults(type) {
      const defaults = DEFAULTS_BY_TYPE[type];
      if (!defaults) return;
      this.form.trigger = defaults.trigger;
      // Solo reiniciar el borrador actual, no las reglas/recompensas ya añadidas.
      this.currentRule = { ...blankRule(), ...defaults.rule };
      this.currentReward = { ...blankReward(), ...defaults.reward };
    },
    initializeForm() {
      if (this.program) {
        this.isEditing = true;
        let parsedOptions = [];
        if (this.program.extra_options) {
          try {
            const raw = typeof this.program.extra_options === "string"
              ? this.program.extra_options
              : this.program.extra_options.String || "";
            parsedOptions = raw ? JSON.parse(raw) : [];
          } catch (e) {
            parsedOptions = [];
          }
        }
        this.form = {
          id: this.program.id,
          name: this.program.name || "",
          program_type: this.program.program_type || "coupons",
          trigger: this.program.trigger || "with_code",
          max_usage: this.program.max_usage || 0,
          active: this.program.active !== undefined ? this.program.active : true,
          limit_usage: this.program.limit_usage !== undefined ? this.program.limit_usage : false,
          portal_visible: this.program.portal_visible !== undefined ? this.program.portal_visible : false,
          require_extra: this.program.require_extra !== undefined ? this.program.require_extra : false,
          extra_description: this.program.extra_description || "",
          extra_type: this.extractNullable(this.program.extra_type) || "text",
          extra_regex: this.extractNullable(this.program.extra_regex) || "",
          extra_options: parsedOptions,
          discount_line_product_id: this.program.discount_line_product_id || "",
          rules: [],
          rewards: []
        };
        if (this.program.rules && this.program.rules.length > 0) {
          this.form.rules = JSON.parse(JSON.stringify(this.program.rules));
        }
        if (this.program.rewards && this.program.rewards.length > 0) {
          this.form.rewards = JSON.parse(JSON.stringify(this.program.rewards));
        }
      } else {
        this.isEditing = false;
        this.form = {
          name: "",
          program_type: "coupons",
          trigger: "with_code",
          max_usage: 0,
          active: true,
          limit_usage: true,
          portal_visible: false,
          require_extra: false,
          extra_description: "",
          extra_type: "text",
          extra_regex: "",
          extra_options: [],
          discount_line_product_id: "",
          rules: [],
          rewards: []
        };
        this.applyTypeDefaults(this.form.program_type);
      }
    },
    extractNullable(v) {
      if (v === null || v === undefined) return "";
      if (typeof v === "string") return v;
      if (typeof v === "object" && "String" in v) return v.Valid ? v.String : "";
      return "";
    },

    validateRewardBeforeAdd() {
      if (this.currentReward.reward_type === "discount") {
        if (!this.currentReward.discount || this.currentReward.discount <= 0) {
          this.makeToast("Error", "El descuento debe ser mayor a 0", "danger");
          return false;
        }
        if (this.currentReward.discount_mode === "percent" && this.currentReward.discount > 100) {
          this.makeToast("Error", "El porcentaje no puede ser mayor a 100", "danger");
          return false;
        }
      }
      if (this.currentReward.reward_type === "free_product") {
        if (!this.currentReward.reward_product_id || !this.currentReward.reward_product_qty) {
          this.makeToast("Error", "Debe seleccionar un producto y cantidad para la recompensa", "danger");
          return false;
        }
      }
      if (!this.currentReward.description.trim()) {
        this.makeToast("Error", "La descripción de la recompensa no puede estar vacía", "danger");
        return false;
      }
      if (!this.currentReward.required_points || this.currentReward.required_points < 1) {
        this.makeToast("Error", "Los puntos requeridos deben ser al menos 1", "danger");
        return false;
      }
      return true;
    },

    validateRuleBeforeAdd() {
      if (this.ruleNeedsCode) {
        if (!this.currentRule.code || !this.currentRule.code.trim()) {
          this.makeToast("Error", "El código de promoción no puede estar vacío", "danger");
          return false;
        }
        if (this.form.rules.some(r => r.code === this.currentRule.code)) {
          this.makeToast("Error", "Ese código ya existe en este programa", "danger");
          return false;
        }
      }
      if (this.currentRule.reward_pointmode === "unit") {
        if (!this.currentRule.minimum_qty || this.currentRule.minimum_qty < 1) {
          this.makeToast("Error", "La cantidad mínima debe ser al menos 1", "danger");
          return false;
        }
      }
      if (!this.currentRule.reward_point_amount || this.currentRule.reward_point_amount < 1) {
        this.makeToast("Error", "Los puntos otorgados deben ser al menos 1", "danger");
        return false;
      }
      if (this.currentRule.minimum_amount < 0) {
        this.makeToast("Error", "El monto mínimo no puede ser negativo", "danger");
        return false;
      }
      return true;
    },

    rulePayload(rule) {
      return {
        program_id: this.form.id,
        reward_pointmode: rule.reward_pointmode,
        mode: "with_code",
        code: this.ruleNeedsCode ? rule.code : "",
        minimum_amount: rule.minimum_amount || 0,
        minimum_qty: rule.minimum_qty,
        active: rule.active,
        reward_point_amount: rule.reward_point_amount
      };
    },

    rewardPayload(reward) {
      return {
        program_id: this.form.id,
        reward_type: reward.reward_type,
        discount_mode: reward.discount_mode,
        discount_applicability: reward.discount_applicability,
        description: reward.description,
        discount_max_amount: reward.discount_max_amount,
        active: reward.active,
        discount: reward.discount,
        required_points: reward.required_points,
        reward_product_id: reward.reward_product_id,
        reward_product_qty: reward.reward_product_qty
      };
    },

    async addRule() {
      if (!this.validateRuleBeforeAdd()) return;
      if (!this.form.id) {
        this.makeToast("Error", "Primero debe crear el programa", "danger");
        return;
      }
      try {
        const response = await axios.post(`/loyalty/program/${this.form.id}/rule`, this.rulePayload(this.currentRule));
        this.form.rules.push({ ...this.currentRule, id: response.data.id || Date.now() });
        this.currentRule = { ...blankRule(), ...(DEFAULTS_BY_TYPE[this.form.program_type] || {}).rule };
        this.makeToast("Éxito", "Regla añadida correctamente", "success");
      } catch (error) {
        console.error("Error al añadir la regla:", error);
        this.makeToast("Error", `Error al añadir la regla: ${error.message}`, "danger");
      }
    },

    removeRule(index) {
      this.form.rules.splice(index, 1);
    },

    async addReward() {
      if (!this.validateRewardBeforeAdd()) return;
      if (!this.form.id) {
        this.makeToast("Error", "Primero debe crear el programa", "danger");
        return;
      }
      try {
        const response = await axios.post(`/loyalty/program/${this.form.id}/reward`, this.rewardPayload(this.currentReward));
        this.form.rewards.push({ ...this.currentReward, id: response.data.id || Date.now() });
        this.currentReward = { ...blankReward(), ...(DEFAULTS_BY_TYPE[this.form.program_type] || {}).reward };
        this.makeToast("Éxito", "Recompensa añadida correctamente", "success");
      } catch (error) {
        console.error("Error al añadir la recompensa:", error);
        this.makeToast("Error", `Error al añadir la recompensa: ${error.message}`, "danger");
      }
    },

    removeReward(index) {
      this.form.rewards.splice(index, 1);
    },

    async saveRules() {
      if (!this.form.id) {
        this.makeToast("Error", "Primero debe crear el programa", "danger");
        return;
      }
      if (this.form.rules.length === 0) {
        this.makeToast("Error", "Debe añadir al menos una regla", "danger");
        return;
      }
      try {
        for (const rule of this.form.rules) {
          if (rule.id && rule.id > 0) continue;
          const response = await axios.post(`/loyalty/program/${this.form.id}/rule`, this.rulePayload(rule));
          rule.id = response.data.id;
        }
        this.makeToast("Éxito", "Reglas guardadas correctamente", "success");
      } catch (error) {
        console.error("Error al guardar las reglas:", error);
        this.makeToast("Error", `Error al guardar las reglas: ${error.message}`, "danger");
      }
    },

    async saveRewards() {
      if (!this.form.id) {
        this.makeToast("Error", "Primero debe crear el programa", "danger");
        return;
      }
      if (this.form.rewards.length === 0) {
        this.makeToast("Error", "Debe añadir al menos una recompensa", "danger");
        return;
      }
      try {
        for (const reward of this.form.rewards) {
          if (reward.id && reward.id > 0) continue;
          const response = await axios.post(`/loyalty/program/${this.form.id}/reward`, this.rewardPayload(reward));
          reward.id = response.data.id;
        }
        this.makeToast("Éxito", "Recompensas guardadas correctamente", "success");
      } catch (error) {
        console.error("Error al guardar las recompensas:", error);
        this.makeToast("Error", `Error al guardar las recompensas: ${error.message}`, "danger");
      }
    },

    programPayload() {
      return {
        name: this.form.name,
        max_usage: this.form.max_usage,
        program_type: this.form.program_type,
        trigger: this.form.trigger,
        active: this.form.active,
        limit_usage: this.form.limit_usage,
        portal_visible: this.form.portal_visible,
        require_extra: this.form.require_extra,
        extra_description: this.form.extra_description,
        extra_type: this.form.require_extra ? this.form.extra_type : null,
        extra_regex: this.form.require_extra && this.form.extra_regex ? this.form.extra_regex : null
      };
    },

    async createProgramOnly() {
      if (!this.form.name.trim()) {
        this.makeToast("Error", "El nombre del programa no puede estar vacío", "danger");
        return;
      }
      try {
        if (this.isEditing) {
          await axios.put(`/loyalty/program/${this.form.id}`, this.programPayload());
        } else {
          const response = await axios.post("/loyalty/program", this.programPayload());
          this.form.id = response.data.id;
          this.isEditing = true;
        }
        this.makeToast("Éxito", "Programa guardado correctamente", "success");
        this.$emit("saved");
      } catch (error) {
        console.error("Error al guardar el programa:", error);
        this.makeToast("Error", `Error al guardar el programa: ${error.message}`, "danger");
      }
    },

    async submitForm() {
      if (!this.form.name.trim()) {
        this.makeToast("Error", "El nombre del programa no puede estar vacío", "danger");
        return;
      }
      if (this.form.rules.length === 0) {
        this.makeToast("Error", "Debe añadir al menos una regla al programa", "danger");
        return;
      }
      if (this.form.rewards.length === 0) {
        this.makeToast("Error", "Debe añadir al menos una recompensa al programa", "danger");
        return;
      }
      try {
        let response;
        if (this.isEditing) {
          response = await axios.put(`/loyalty/program/${this.form.id}`, this.programPayload());
        } else {
          response = await axios.post("/loyalty/program", this.programPayload());
        }
        const programId = this.isEditing ? this.form.id : response.data.id;
        for (const rule of this.form.rules) {
          if (rule.id && rule.id > 0) continue;
          const ruleRes = await axios.post(`/loyalty/program/${programId}/rule`, this.rulePayload(rule));
          rule.id = ruleRes.data.id;
        }
        for (const reward of this.form.rewards) {
          if (reward.id && reward.id > 0) continue;
          const rewardRes = await axios.post(`/loyalty/program/${programId}/reward`, this.rewardPayload(reward));
          reward.id = rewardRes.data.id;
        }
        this.$emit("saved");
      } catch (error) {
        console.error("Error al guardar el programa:", error);
        this.makeToast("Error", `Error al guardar el programa: ${error.message}`, "danger");
      }
    },

    async addRuleProduct(rule) {
      if (!this.newRuleProductId) return;
      try {
        const response = await axios.post(`/loyalty/rule/${rule.id}/product`, {
          product_id: this.newRuleProductId
        });
        if (!rule.rule_products) rule.rule_products = [];
        rule.rule_products.push(response.data);
        this.newRuleProductId = null;
        this.$forceUpdate();
        this.makeToast("Éxito", "Producto agregado a la regla", "success");
      } catch (error) {
        this.makeToast("Error", "Error al agregar producto: " + error.message, "danger");
      }
    },

    async removeRuleProduct(rule, index, rpId) {
      try {
        await axios.delete(`/loyalty/rule/product/${rpId}`);
        rule.rule_products.splice(index, 1);
        this.$forceUpdate();
        this.makeToast("Éxito", "Producto eliminado de la regla", "success");
      } catch (error) {
        this.makeToast("Error", "Error al eliminar producto: " + error.message, "danger");
      }
    },

    async addExtraOption() {
      if (!this.newExtraOption || !this.form.id) return;
      try {
        await axios.post(`/loyalty/program/${this.form.id}/option`, { option: this.newExtraOption });
        if (!this.form.extra_options.includes(this.newExtraOption)) {
          this.form.extra_options.push(this.newExtraOption);
        }
        this.newExtraOption = "";
        this.makeToast("Éxito", "Opción añadida", "success");
      } catch (error) {
        this.makeToast("Error", "Error al añadir opción: " + error.message, "danger");
      }
    },

    async removeExtraOption(option, idx) {
      try {
        await axios.delete(`/loyalty/program/${this.form.id}/option/${encodeURIComponent(option)}`);
        this.form.extra_options.splice(idx, 1);
        this.makeToast("Éxito", "Opción eliminada", "success");
      } catch (error) {
        this.makeToast("Error", "Error al eliminar opción: " + error.message, "danger");
      }
    },

    makeToast(title, content, variant = "default") {
      this.$bvToast.toast(content, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: 5000
      });
    }
  }
};
</script>

<style scoped>
/* Estilos adicionales pueden ser agregados aquí */
</style>
